import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { adminService, ChallengeTranslation } from '../services/adminService'
import { useTranslation } from '../i18n/hooks/useTranslation'
import { Button } from './ui/Button'

interface ChallengeTranslationModalProps {
  challengeId: string
  onClose: () => void
}

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'French' },
  { code: 'es', name: 'Spanish' },
  { code: 'de', name: 'German' },
]

export function ChallengeTranslationModal({ challengeId, onClose }: ChallengeTranslationModalProps) {
  const { t } = useTranslation()
  const [editingTranslation, setEditingTranslation] = useState<ChallengeTranslation | null>(null)
  const queryClient = useQueryClient()

  const { data: translations = [], isLoading } = useQuery({
    queryKey: ['challengeTranslations', challengeId],
    queryFn: () => adminService.getChallengeTranslations(challengeId),
  })

  const { register, handleSubmit, reset, formState: { errors } } = useForm<{
    language: string
    name: string
    description: string
  }>()

  const createMutation = useMutation({
    mutationFn: (translation: Omit<ChallengeTranslation, 'id'>) =>
      adminService.createChallengeTranslation(translation),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challengeTranslations', challengeId] })
      toast.success('Translation created successfully')
      reset()
    },
    onError: () => {
      toast.error('Failed to create translation')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<ChallengeTranslation> }) =>
      adminService.updateChallengeTranslation(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challengeTranslations', challengeId] })
      toast.success('Translation updated successfully')
      setEditingTranslation(null)
      reset()
    },
    onError: () => {
      toast.error('Failed to update translation')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminService.deleteChallengeTranslation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challengeTranslations', challengeId] })
      toast.success('Translation deleted successfully')
    },
    onError: () => {
      toast.error('Failed to delete translation')
    },
  })

  const onSubmit = (data: { language: string; name: string; description: string }) => {
    if (editingTranslation) {
      updateMutation.mutate({
        id: editingTranslation.id,
        updates: { ...data }
      })
    } else {
      createMutation.mutate({
        challengeId,
        ...data
      })
    }
  }

  const handleEdit = (translation: ChallengeTranslation) => {
    setEditingTranslation(translation)
    reset({
      language: translation.language,
      name: translation.name,
      description: translation.description,
    })
  }

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this translation?')) {
      deleteMutation.mutate(id)
    }
  }

  const getLanguageName = (code: string) => {
    return LANGUAGES.find(lang => lang.code === code)?.name || code
  }

  const existingLanguages = translations.map(t => t.language)
  const availableLanguages = LANGUAGES.filter(lang => !existingLanguages.includes(lang.code))

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">
              Challenge Translations: {challengeId}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Translation Form */}
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingTranslation ? t('translations.editTranslation') : t('translations.addTranslation')}
            </h3>
            
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Language
                </label>
                <select
                  {...register('language', { required: 'Language is required' })}
                  disabled={!!editingTranslation}
                  className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                >
                  <option value="">Select language</option>
                  {(editingTranslation ? LANGUAGES : availableLanguages).map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
                {errors.language && (
                  <p className="text-red-500 text-sm mt-1">{errors.language.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  {...register('name', { required: 'Name is required' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  rows={3}
                  {...register('description', { required: 'Description is required' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                {errors.description && (
                  <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                {editingTranslation && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingTranslation(null)
                      reset()
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 bg-primary-600 text-white rounded-full hover:bg-primary-700 disabled:opacity-50 transition-colors"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingTranslation
                    ? 'Update'
                    : 'Add'
                  }
                </button>
              </div>
            </form>
          </div>

          {/* Existing Translations */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Existing Translations ({translations.length})
            </h3>
            
            {isLoading ? (
              <div className="text-center py-8 text-gray-500">Loading translations...</div>
            ) : translations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No translations found. Add the first translation above.
              </div>
            ) : (
              <div className="space-y-4">
                {translations.map((translation) => (
                  <div key={translation.id} className="border border-gray-200 rounded-full p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <span className="text-sm font-medium text-gray-900">
                            {getLanguageName(translation.language)}
                          </span>
                          <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            {translation.language}
                          </span>
                        </div>
                        <h4 className="text-lg font-medium text-gray-900 mb-1">
                          {translation.name}
                        </h4>
                        <p className="text-gray-600 text-sm">
                          {translation.description}
                        </p>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <Button
                          onClick={() => handleEdit(translation)}
                          variant="tertiary"
                          size="sm"
                        >
                          {t('common.edit')}
                        </Button>
                        <button
                          onClick={() => handleDelete(translation.id)}
                          className="text-red-600 hover:text-red-900 text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}